/**
 * Transformaciones puras del menú: del DTO de Identity a `MenuGroup[]` y la deduplicación de
 * módulos/secciones/páginas. `MenuService` solo orquesta (HTTP, permisos, orden); la lógica vive aquí.
 */
import { MenuGroup, MenuPage, MenuPermission, MenuSection } from '../models/menu-item.model';
import {
    IdentityMenuModuleDto,
    IdentityMenuPageDto,
    IdentityMenuPermissionDto,
    IdentityMenuSectionDto,
} from '../models/auth-api.model';

/** Ids sintéticos para entradas de Identity sin id propio. */
const GENERATED_SECTION_ID_BASE = 4000;
const GENERATED_PAGE_ID_BASE = 9000;

/** `primary ?? fallback` en minúsculas y sin espacios: clave de comparación de identificadores. */
export function pickIdentifier(primary?: string, fallback?: string): string {
    return (primary ?? fallback ?? '').toLowerCase().trim();
}

/** Lo que el mapeo necesita del servicio (rutas del catálogo e iconos). */
export interface IdentityMenuMapDeps {
    resolveRouterLink: (
        moduleIdentifier: string,
        sectionIdentifier: string,
        pageIdentifier: string,
        pageName?: string,
    ) => string | undefined;
    iconForRoute: (routerLink: string) => string | undefined;
    iconForModule: (moduleIdentifier: string) => string;
}

/** Contadores de ids sintéticos compartidos por toda una pasada de mapeo. */
interface IdIssuer {
    nextSectionId: () => number;
    nextPageId: () => number;
}

function createIdIssuer(): IdIssuer {
    let sectionId = GENERATED_SECTION_ID_BASE;
    let pageId = GENERATED_PAGE_ID_BASE;
    return { nextSectionId: () => sectionId++, nextPageId: () => pageId++ };
}

export function mapIdentityPermissions(permissions: IdentityMenuPermissionDto[]): MenuPermission[] {
    return permissions.map((permission, index) => ({
        id: index + 1,
        name: permission.permissionIdentifier || permission.action || 'Permiso',
        action: permission.action || 'read',
    }));
}

/** La sección de auditoría retail de Mercadeo siempre se muestra con el mismo nombre. */
export function resolveSectionLabel(moduleIdentifier: string, sectionIdentifier: string, fallbackLabel: string): string {
    if (
        moduleIdentifier.toLowerCase().trim() === 'mercadeo' &&
        sectionIdentifier.toLowerCase().trim() === 'auditoria-retail'
    ) {
        return 'Auditoria Retail';
    }

    return fallbackLabel;
}

function mapIdentityPage(
    page: IdentityMenuPageDto,
    moduleIdentifier: string,
    sectionIdentifier: string,
    deps: IdentityMenuMapDeps,
    ids: IdIssuer,
): MenuPage | null {
    const pageIdentifier = pickIdentifier(page.permissionIdentifier, page.name);
    const routerLink = deps.resolveRouterLink(moduleIdentifier, sectionIdentifier, pageIdentifier, page.name);
    if (!routerLink) {
        return null;
    }

    return {
        id: page.id ?? ids.nextPageId(),
        label: page.name?.trim() || pageIdentifier || 'Sin nombre',
        routerLink,
        icon: deps.iconForRoute(routerLink),
        permissions: mapIdentityPermissions(page.permissions ?? []),
        disabled: false,
    };
}

function mapIdentitySection(
    section: IdentityMenuSectionDto,
    moduleIdentifier: string,
    deps: IdentityMenuMapDeps,
    ids: IdIssuer,
): MenuSection | null {
    const sectionIdentifier = pickIdentifier(section.permissionIdentifier, section.name);
    const pages: MenuPage[] = [];
    for (const page of section.pages ?? []) {
        const mapped = mapIdentityPage(page, moduleIdentifier, sectionIdentifier, deps, ids);
        if (mapped) {
            pages.push(mapped);
        }
    }

    if (pages.length === 0) {
        return null;
    }

    return {
        id: section.id ?? ids.nextSectionId(),
        label: resolveSectionLabel(
            moduleIdentifier,
            sectionIdentifier,
            section.name?.trim() || sectionIdentifier || 'Seccion',
        ),
        pages,
    };
}

function mapIdentityModule(
    module: IdentityMenuModuleDto,
    moduleIndex: number,
    deps: IdentityMenuMapDeps,
    ids: IdIssuer,
): MenuGroup | null {
    const moduleIdentifier = pickIdentifier(module.permissionIdentifier, module.name);
    const sections: MenuSection[] = [];
    for (const section of module.sections ?? []) {
        const mapped = mapIdentitySection(section, moduleIdentifier, deps, ids);
        if (mapped) {
            sections.push(mapped);
        }
    }

    if (sections.length === 0) {
        return null;
    }

    return {
        id: module.id ?? moduleIndex + 1,
        label: module.name?.trim() || moduleIdentifier || 'Modulo',
        icon: deps.iconForModule(moduleIdentifier || module.name || ''),
        isGroup: true,
        sections,
    };
}

/** Módulos de Identity → grupos de menú; descarta páginas sin ruta y secciones/módulos vacíos. */
export function mapIdentityMenuToGroups(modules: IdentityMenuModuleDto[], deps: IdentityMenuMapDeps): MenuGroup[] {
    const ids = createIdIssuer();
    const groups: MenuGroup[] = [];
    modules.forEach((module, moduleIndex) => {
        const group = mapIdentityModule(module, moduleIndex, deps, ids);
        if (group) {
            groups.push(group);
        }
    });
    return groups;
}

function labelKey(label: string): string {
    return label.trim().toLowerCase();
}

/** Une grupos con la misma etiqueta y, dentro de ellos, secciones con la misma etiqueta (sin mutar la entrada). */
export function mergeGroupsByLabel(groups: MenuGroup[]): MenuGroup[] {
    const merged = new Map<string, MenuGroup>();

    for (const group of groups) {
        const key = labelKey(group.label);
        const existing = merged.get(key);
        if (!existing) {
            merged.set(key, {
                ...group,
                sections: group.sections.map((section) => ({ ...section, pages: [...section.pages] })),
            });
            continue;
        }

        for (const section of group.sections) {
            const sectionKey = labelKey(section.label);
            const existingSection = existing.sections.find((item) => labelKey(item.label) === sectionKey);
            if (existingSection) {
                existingSection.pages.push(...section.pages);
            } else {
                existing.sections.push({ ...section, pages: [...section.pages] });
            }
        }
    }

    return Array.from(merged.values());
}

/** Clave de una página: su ruta, o `sección:etiqueta` si no tiene. */
function pageKey(sectionLabel: string, page: MenuPage): string {
    const routeKey = (page.routerLink ?? '').trim().toLowerCase();
    return routeKey || `${sectionLabel}:${page.label}`.trim().toLowerCase();
}

/** Deja cada página una sola vez en todo el menú (gana la primera) y poda secciones/grupos vacíos. */
export function dropDuplicatePages(groups: MenuGroup[]): MenuGroup[] {
    const seen = new Set<string>();
    const result: MenuGroup[] = [];

    for (const group of groups) {
        const sections: MenuSection[] = [];
        for (const section of group.sections) {
            const pages = section.pages.filter((page) => {
                const key = pageKey(section.label, page);
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            });
            if (pages.length > 0) {
                sections.push({ ...section, pages });
            }
        }
        if (sections.length > 0) {
            result.push({ ...group, sections });
        }
    }

    return result;
}

/** Une grupos/secciones repetidos y elimina páginas duplicadas. */
export function dedupeMenuGroups(groups: MenuGroup[]): MenuGroup[] {
    return dropDuplicatePages(mergeGroupsByLabel(groups));
}
